import styled from 'styled-components';

import { NfzDepartment } from '@dreryk/egabinet-nfz-configuration-frontend-api';

import { createTranslatedText, FormContextValues } from '@dreryk/react-common';
import {
   FormLabel,
   Loader,
   LoaderSize,
   resolveInputLayoutStyles,
   Select,
   useSelectContext,
} from '@dreryk/react-components';

import { patientTranslationKeys } from '../../translations';
import { useGetNfzDepartmentsQuery } from '../common.hooks';
import { createNfzDepartmentFilter } from '../common.utils';

type PartialFormBean = {
   nfzDepartment: NfzDepartment | null;
};

type NfzDepartmentFieldProps = Pick<FormContextValues<PartialFormBean>, 'register'>;

const triggerLabelExtractor = (department?: NfzDepartment): string =>
   department ? `${department.name} (${department.code})` : '';

const StyledNfzDepartmentField = styled.div`
   ${resolveInputLayoutStyles}
`;

StyledNfzDepartmentField.displayName = 'StyledNfzDepartmentField';

const StyledSelect = styled(Select)`
   min-width: 200px;
` as typeof Select;

type NfzDepartmentsListProps = {
   readonly departments: NfzDepartment[];
};
const NfzDepartmentsList = (props: NfzDepartmentsListProps): JSX.Element => {
   const { searchText } = useSelectContext();
   const items = props.departments.filter(createNfzDepartmentFilter(searchText));
   if (!items.length) {
      return <Select.NoItems testId={'noCountries'} />;
   }
   return (
      <Select.List items={props.departments.filter(createNfzDepartmentFilter(searchText))} keyExtractor={'id'}>
         {(department: NfzDepartment) => (
            <Select.Item
               item={department}
               testId={department.code}
               label={
                  <span>
                     <Select.Highlight node={createTranslatedText(department.name)} />
                     <span>
                        {' ('}
                        <Select.Highlight node={createTranslatedText(department.code)} />
                        {')'}
                     </span>
                  </span>
               }
            />
         )}
      </Select.List>
   );
};

export const NfzDepartmentField = (props: NfzDepartmentFieldProps): JSX.Element | null => {
   const { data: nfzDepartments, loading, fetched } = useGetNfzDepartmentsQuery();

   if (loading && !fetched) {
      return (
         <StyledNfzDepartmentField>
            <FormLabel
               label={patientTranslationKeys.nfzDepartment.fetchingDepartments}
               testId={'fetchingDepartments'}
            />
            <Loader size={LoaderSize.MEDIUM} testId={'loader'} />
         </StyledNfzDepartmentField>
      );
   }

   if (!loading && nfzDepartments) {
      return (
         <StyledSelect<NfzDepartment>
            triggerLabelExtractor={triggerLabelExtractor}
            controller={props.register('nfzDepartment')}
            label={patientTranslationKeys.nfzDepartment.label}
            placeholder={patientTranslationKeys.nfzDepartment.choose}
            testId={'nfzDepartment'}
            searchInputEnabled
         >
            <NfzDepartmentsList departments={nfzDepartments} />
         </StyledSelect>
      );
   }
   return null;
};
